import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import { useProducts } from '@/features/quotes/hooks/common/use-products.hook';
import { usePmeTarifacao } from '@/features/proposal-analyses/pme/use-pme-tarifacao.hook';
import { useDownloadGroupLifeSignedCommercialLetter } from '@/features/proposals/hooks/group-life/use-download-group-life-signed-commercial-letter.hook';
import { isServiceCoverage } from '@/features/quotes/utils/group-life/service-coverage.util';
import { dailyAllowanceDetails } from '@/features/quotes/utils/group-life/daily-allowance.util';
import type { PolicyDetailsResponse } from '@/services/policies/types';
import { PolicyStatus } from '@/shared/enums/policy-status.enum';
import { useNavigation } from '@/shared/hooks';
import { Page, PageHeader } from '@/shared/ui/page';
import { Button, TabsComponent } from '@noorden-insurtech/alba-design-system';
import { Home } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelectedBrokerWriteGuard } from '../../../brokers/hooks';
import { CapitalGlobalBillingTab } from '../capital-global/capital-global-billing-tab.component';
import { DownloadPdfDialog } from '../download-pdf.dialog';
import { useInstallments } from '../../hooks/use-installments.hook';
import { usePolicyPdf } from '../../hooks/use-policy-pdf.hook';
import { useStarterKit } from '../../hooks/use-starter-kit.hook';
import { usePmePolicyProposal } from './hooks/use-pme-policy-proposal.hook';
import { mapPolicyToPmeView } from './map-policy-to-pme-view.util';
import { PmePolicyPremiumCard } from './pme-policy-premium-card.component';
import { PmePolicyTab } from './pme-policy-tab.component';

type Props = {
  policyId: string;
  /** Já carregado pelo resolver da rota — evita refetch de `policy-details`. */
  policy: PolicyDetailsResponse;
};

const policyStatusLabel: Record<number, string> = {
  [PolicyStatus.Effective]: 'Emitida',
  [PolicyStatus.Rejected]: 'Cancelada',
};

/** Todas as vidas num único page fetch alimentam as médias de cobertura. */
const PRICING_PAGE_SIZE = 1000;

type DownloadDialogState = {
  open: boolean;
  title: string;
  message: string;
  action: (() => void) | null;
};

const initialDialogState: DownloadDialogState = {
  open: false,
  title: 'Baixar PDF',
  message: '',
  action: null,
};

export function PmePolicyDetails({ policyId, policy }: Props) {
  const [activeTab, setActiveTab] = useState('policy');
  const [dialog, setDialog] = useState<DownloadDialogState>(initialDialogState);
  const { push } = useNavigation();

  const proposalQuery = usePmePolicyProposal(policyId);
  const proposal = proposalQuery.data;

  // Tarifação da apólice inteira (sem `targetId`): é dela que saem o capital
  // total, as taxas médias por cobertura e o corretor do cabeçalho.
  const pricingQuery = usePmeTarifacao(
    policyId,
    'proposal',
    undefined,
    PRICING_PAGE_SIZE
  );
  const pricingLives = useMemo(
    () => pricingQuery.data?.pages.flatMap(page => page.data) ?? [],
    [pricingQuery.data]
  );
  const parties = pricingQuery.data?.pages[0]?.parties;
  const livesTotal = pricingQuery.data?.pages[0]?.livesTotal;

  const installmentsQuery = useInstallments(policyId);
  const { data: products } = useProducts();

  const coverageSchemaQuery = useGroupLifeCoverageSchema({
    productCode: proposal?.productCode ?? policy.productCode,
  });
  const coverageNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const coverage of coverageSchemaQuery.data?.coverages ?? []) {
      map.set(coverage.code, coverage.name);
    }
    return map;
  }, [coverageSchemaQuery.data]);
  const serviceCoverageKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const coverage of coverageSchemaQuery.data?.coverages ?? []) {
      if (isServiceCoverage(coverage.code, coverage.name, coverage.group)) {
        keys.add(coverage.code);
        keys.add(coverage.name);
      }
    }
    return keys;
  }, [coverageSchemaQuery.data]);
  const dailyAllowanceByCoverage = useMemo(
    () =>
      dailyAllowanceDetails(coverageSchemaQuery.data, proposal?.plan?.coverages),
    [coverageSchemaQuery.data, proposal?.plan?.coverages]
  );

  const statusLabel = policyStatusLabel[policy.policyStatus ?? 0] ?? 'Emitida';
  const productName =
    products?.find(product => product.code === policy.productCode)?.name ??
    'PME';

  const view = useMemo(
    () =>
      mapPolicyToPmeView({
        policy,
        proposal,
        pricingLives,
        livesCount: livesTotal,
        parties,
        installments: installmentsQuery.data,
        coverageNameByCode,
        serviceCoverageKeys,
        dailyAllowanceByCoverage,
        productName,
        statusLabel,
      }),
    [
      dailyAllowanceByCoverage,
      policy,
      proposal,
      pricingLives,
      livesTotal,
      parties,
      installmentsQuery.data,
      coverageNameByCode,
      serviceCoverageKeys,
      productName,
      statusLabel,
    ]
  );

  const policyNumber = view.policyNumber;

  const policyPdf = usePolicyPdf();
  const starterKit = useStarterKit();
  const commercialLetter = useDownloadGroupLifeSignedCommercialLetter(policyId);
  // Corretora inativa: boletos travados (a Api barra com 403), como no CG.
  const policyActionGuard = useSelectedBrokerWriteGuard('policy', 'other');

  const isPendingDownload =
    policyPdf.isPending || starterKit.isPending || commercialLetter.isPending;
  const isErrorDownload =
    policyPdf.isError || starterKit.isError || commercialLetter.isError;
  // A Api explica quando a carta assinada ainda não chegou da DocuSign.
  const downloadErrorMessage = commercialLetter.error?.message;

  function openDownloadDialog(
    title: string,
    message: string,
    action: () => void
  ) {
    policyPdf.reset();
    starterKit.reset();
    commercialLetter.reset();
    setDialog({ open: true, title, message, action });
  }

  function closeDialog() {
    setDialog(initialDialogState);
  }

  useEffect(() => {
    if (
      (policyPdf.isSuccess ||
        starterKit.isSuccess ||
        commercialLetter.isSuccess) &&
      dialog.open
    ) {
      closeDialog();
      policyPdf.reset();
      starterKit.reset();
      commercialLetter.reset();
    }
  }, [policyPdf.isSuccess, starterKit.isSuccess, commercialLetter.isSuccess]);

  return (
    <Page.Root>
      <DownloadPdfDialog
        isOpen={dialog.open}
        onClose={closeDialog}
        onConfirm={() => dialog.action?.()}
        isLoading={isPendingDownload}
        isError={isErrorDownload}
        title={dialog.title}
        message={dialog.message}
        errorMessage={downloadErrorMessage}
      />

      <Page.Breadcrumb
        items={[
          {
            label: 'Pagina Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Apólices', href: '/policies' },
          { label: `Apólice Nº ${policyNumber}`, active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              <span className='block max-w-full text-[1.7rem] leading-[1.15] whitespace-nowrap sm:text-[3rem]'>
                Apólice Nº {policyNumber}
              </span>
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados e registros da inspeção para validação da
              proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
        </PageHeader.Main>
      </PageHeader.Root>

      <TabsComponent.Root
        value={activeTab}
        onValueChange={details => setActiveTab(details.value)}
      >
        <TabsComponent.List className='grid grid-cols-2'>
          <TabsComponent.Trigger value='policy'>Apólice</TabsComponent.Trigger>
          <TabsComponent.Trigger value='billing'>
            Cobrança
          </TabsComponent.Trigger>
        </TabsComponent.List>

        <TabsComponent.Content value='policy'>
          <div className='mt-6'>
            <PmePolicyTab policyId={policyId} view={view} />
          </div>

          <div className='flex flex-col gap-2 pt-4 pb-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pt-10'>
            <button
              type='button'
              onClick={() => push('/policies')}
              className='order-2 cursor-pointer self-center rounded-lg border border-alba-gray-200/70 bg-transparent px-4 py-2 text-sm font-bold text-alba-blue-500 hover:underline sm:order-1 sm:self-start sm:border-0 sm:px-0 sm:py-0'
            >
              Sair
            </button>
            <div className='order-1 flex w-full flex-col gap-3 sm:order-2 sm:w-auto sm:flex-row'>
              <Button
                size='sm'
                className='w-full sm:w-auto'
                onClick={() =>
                  openDownloadDialog(
                    'Baixar PDF',
                    'O PDF da apólice será baixado para o seu dispositivo.',
                    () => policyPdf.mutate({ policyId, policyNo: policyNumber })
                  )
                }
              >
                Baixar PDF
              </Button>
              <Button
                size='sm'
                className='w-full sm:w-auto'
                onClick={() =>
                  openDownloadDialog(
                    'Carta Comercial',
                    'A carta comercial assinada será baixada para o seu dispositivo.',
                    () => commercialLetter.mutate()
                  )
                }
              >
                Carta Comercial
              </Button>
              <Button
                size='sm'
                className='w-full sm:w-auto'
                onClick={() =>
                  openDownloadDialog(
                    'Baixar Kit da Apólice',
                    'O kit da apólice será baixado para o seu dispositivo.',
                    () =>
                      starterKit.mutate({ policyId, policyNo: policyNumber })
                  )
                }
              >
                Baixar Kit da Apólice
              </Button>
            </div>
          </div>
        </TabsComponent.Content>

        <TabsComponent.Content value='billing'>
          {/* Cobrança do PME = a do Capital Global (Query API do i4Pro, uma
              linha por subestipulante), com o mesmo card MENSAL da aba Apólice
              — o padrão mostrava o prêmio anual do InsureMO. */}
          <CapitalGlobalBillingTab
            data={policy}
            product={productName}
            proposalNumber={view.premium.quoteNumber}
            status={statusLabel}
            downloadGuard={policyActionGuard}
            premiumCard={<PmePolicyPremiumCard data={view.premium} />}
          />

          <div className='flex justify-center pt-4 pb-8 sm:justify-start sm:pt-10'>
            <button
              type='button'
              onClick={() => push('/policies')}
              className='cursor-pointer rounded-lg border border-alba-gray-200/70 bg-transparent px-4 py-2 text-sm font-bold text-alba-blue-500 hover:underline sm:rounded-none sm:border-0 sm:px-0 sm:py-0'
            >
              Sair
            </button>
          </div>
        </TabsComponent.Content>
      </TabsComponent.Root>
    </Page.Root>
  );
}
